import { useState, useEffect } from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────
const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M20.317 4.369a19.79 19.79 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.1 13.1 0 01-1.872-.892.077.077 0 01-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.009c.12.099.246.198.373.292a.077.077 0 01-.006.127 12.3 12.3 0 01-1.873.891.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.84 19.84 0 006.002-3.03.077.077 0 00.032-.056c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.028zM8.02 15.331c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const TrophyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="22" height="22">
    <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0012 0V2z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="18">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <path d="M15 3h6v6" /><path d="M10 14L21 3" />
  </svg>
);

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    open:     { label: "Åben",     color: "#22c55e" },
    upcoming: { label: "Kommende", color: "#f59e0b" },
    closed:   { label: "Lukket",   color: "#6b7280" },
    live:     { label: "LIVE",     color: "#ef4444" },
  };
  const { label, color } = map[status] || map.upcoming;
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: 4, padding: "2px 9px", fontSize: 11, fontWeight: 700,
      letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      {label}
    </span>
  );
};

// ─── Betingelses-modal (vises før betaling) ───────────────────────────────────
const TermsModal = ({ tournament, user, onConfirm, onClose }) => {
  const [accepted, setAccepted] = useState(false);
  const fee = tournament.fee ?? 20;
  const price = user?.is_member ? 0 : fee;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{tournament.name}</h2>
        <p className="modal__sub">{tournament.game} · {tournament.format ?? "Single Elimination"}</p>

        <div className="modal__rules">
          <h4>Regler & betingelser</h4>
          <ul>
            <li>Tilmeldingsgebyr: <strong>{price === 0 ? "Gratis (medlem)" : `${price} kr`}</strong></li>
            <li>Efter betaling skal du <strong>registrere dig på Challengermode</strong> for at deltage.</li>
            <li>Check-in på Challengermode åbner <strong>30 minutter før</strong> turneringsstart.</li>
            <li>
              <strong>Refundering:</strong> Du kan få dit gebyr refunderet så længe du
              <strong> ikke har checket ind</strong> på Challengermode. Når check-in
              er gennemført (30 min før start), kan gebyret ikke refunderes.
            </li>
            <li>Du skal selv sikre at din Steam-konto og spillernavn er korrekt.</li>
            <li>Usportslig opførsel kan medføre udelukkelse uden refundering.</li>
          </ul>
        </div>

        <label className="modal__accept">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>Jeg har læst og accepterer reglerne og refunderingspolitikken</span>
        </label>

        <div className="modal__actions">
          <button className="btn btn--ghost" onClick={onClose}>Annullér</button>
          <button
            className="btn btn--primary"
            disabled={!accepted}
            onClick={() => onConfirm(tournament)}
          >
            {price === 0 ? "Tilmeld gratis" : `Fortsæt til betaling (${price} kr)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Tournament card ──────────────────────────────────────────────────────────
const TournamentCard = ({ tournament, user, onJoinClick }) => {
  const isMember = user?.is_member;
  const fee = tournament.fee ?? 20;
  const price = isMember ? 0 : fee;
  const alreadyJoined = user && user.steamid && tournament.participants?.includes(user.steamid);
  const needsSteam = user && !user.steamid;
  const cmUrl = tournament.challengermode_url;

  return (
    <div className="t-card">
      <div className="t-card__header">
        <div>
          <StatusBadge status={tournament.status} />
          <h3 className="t-card__title">{tournament.name}</h3>
          <p className="t-card__game">{tournament.game}</p>
        </div>
        <div className="t-card__meta">
          <div className="t-card__meta-item">
            <UserIcon />
            <span>{tournament.participant_count ?? 0}/{tournament.max_participants ?? "∞"}</span>
          </div>
        </div>
      </div>

      <div className="t-card__body">
        <div className="t-card__info-row">
          <span className="t-card__label">Dato</span>
          <span>{new Date(tournament.date).toLocaleDateString("da-DK", {
            day: "numeric", month: "long", year: "numeric",
            hour: "2-digit", minute: "2-digit"
          })}</span>
        </div>
        <div className="t-card__info-row">
          <span className="t-card__label">Format</span>
          <span>{tournament.format ?? "Single Elimination"}</span>
        </div>
        <div className="t-card__info-row">
          <span className="t-card__label">Indmeldingsgebyr</span>
          <span className={price === 0 ? "t-card__free" : "t-card__fee"}>
            {price === 0 ? "Gratis (Medlem)" : `${price} kr`}
          </span>
        </div>

        {(tournament.prize_1 || tournament.prize_2 || tournament.prize_3) && (
          <div className="t-card__prizes">
            <span className="t-card__prizes-label">Præmier</span>
            {tournament.prize_1 && (
              <div className="prize-row">
                <span className="prize-medal prize-medal--gold">🥇</span>
                <span>{tournament.prize_1}</span>
              </div>
            )}
            {tournament.prize_2 && (
              <div className="prize-row">
                <span className="prize-medal prize-medal--silver">🥈</span>
                <span>{tournament.prize_2}</span>
              </div>
            )}
            {tournament.prize_3 && (
              <div className="prize-row">
                <span className="prize-medal prize-medal--bronze">🥉</span>
                <span>{tournament.prize_3}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="t-card__footer">
        {!user ? (
          <a href="/auth/discord/login?type=tournament" className="btn btn--discord">
            <DiscordIcon /> Log ind for at tilmelde
          </a>
        ) : needsSteam ? (
          <a href="/auth/steam?return=/turneringer" className="btn btn--steam">
            🎮 Tilknyt Steam først
          </a>
        ) : alreadyJoined ? (
          <>
            <span className="btn btn--done">✓ Betalt</span>
            {cmUrl && (
              <a href={cmUrl} target="_blank" rel="noreferrer" className="btn btn--primary">
                <ExternalIcon /> Registrér på Challengermode
              </a>
            )}
          </>
        ) : tournament.status === "open" ? (
          <button className="btn btn--primary" onClick={() => onJoinClick(tournament)}>
            {price === 0 ? "Tilmeld gratis" : `Tilmeld (${price} kr)`}
          </button>
        ) : (
          <span className="btn btn--disabled">Tilmelding lukket</span>
        )}
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export default function TournamentPage() {
  const [user, setUser] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payMsg, setPayMsg] = useState(null);
  const [modalTournament, setModalTournament] = useState(null);
  const [confirmedTournament, setConfirmedTournament] = useState(null);

  const loadData = () =>
    Promise.all([
      fetch("/auth/tournament/me", { credentials: "include" }).then((r) => r.json()),
      fetch("/api/tournaments",    { credentials: "include" }).then((r) => r.json()),
    ]).then(([userData, tournamentData]) => {
      if (!userData.error) setUser(userData);
      setTournaments(tournamentData.tournaments ?? []);
      return tournamentData.tournaments ?? [];
    });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (params.get("payment") === "success") {
      setPayMsg("success");
      if (sessionId) {
        // Bekræft betaling, tilmeld, og vis Challengermode-link
        fetch("/api/tournaments/confirm", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        })
          .then((r) => r.json())
          .then((res) => {
            // Find turneringen vi lige betalte for, så vi kan vise CM-link
            loadData().then((tlist) => {
              if (res.tournament_id) {
                const t = tlist.find((x) => String(x.id) === String(res.tournament_id));
                if (t) setConfirmedTournament(t);
              }
            });
          })
          .catch(() => loadData());
        // Ryd query-parametre fra URL
        window.history.replaceState({}, "", "/turneringer");
        setLoading(false);
        return;
      }
    }
    if (params.get("payment") === "cancel") setPayMsg("cancel");

    loadData().finally(() => setLoading(false));
  }, []);

  // Trin 1: åbn betingelses-modal
  const handleJoinClick = (tournament) => setModalTournament(tournament);

  // Trin 2: betingelser accepteret → gratis tilmeld eller Stripe checkout
  const handleConfirm = async (tournament) => {
    setModalTournament(null);
    const isMember = user?.is_member;
    const fee = tournament.fee ?? 20;

    if (isMember || fee === 0) {
      const res = await fetch(`/api/tournaments/${tournament.id}/join`, {
        method: "POST",
        credentials: "include",
      }).then((r) => r.json());

      if (res.ok) {
        await loadData();
        setConfirmedTournament(tournament);
      } else {
        alert(res.error ?? "Noget gik galt");
      }
    } else {
      const res = await fetch(`/api/tournaments/${tournament.id}/checkout`, {
        method: "POST",
        credentials: "include",
      }).then((r) => r.json());

      if (res.url) window.location.href = res.url;
      else alert(res.error ?? "Noget gik galt");
    }
  };

  const cmUrl = confirmedTournament
    ? confirmedTournament.challengermode_url
    : null;

  // Sortering + filtrering:
  //   0) live   (vises altid øverst)
  //   1) mine tilmeldte / betalte
  //   2) åbne
  //   3) kommende
  //   lukkede vises IKKE
  const isJoined = (t) =>
    user && user.steamid && t.participants?.includes(user.steamid);

  const statusRank = (t) => {
    if (t.status === "live") return 0;     // live øverst
    if (isJoined(t)) return 1;             // betalt / tilmeldt
    if (t.status === "open") return 2;     // åbne
    if (t.status === "upcoming") return 3; // kommende
    return 99;
  };

  const sortedTournaments = [...tournaments]
    .filter((t) => t.status !== "closed")  // skjul lukkede
    .sort((a, b) => {
      const r = statusRank(a) - statusRank(b);
      if (r !== 0) return r;
      return new Date(a.date) - new Date(b.date);
    });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&family=Barlow:wght@400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #0d0f14;
          --surface: #14171f;
          --surface2: #1c2030;
          --border: #ffffff12;
          --accent: #e8b43a;
          --accent2: #4f8ef7;
          --discord: #5865f2;
          --text: #e8eaf0;
          --text2: #8b92a8;
          --green: #22c55e;
          --red: #ef4444;
          --radius: 10px;
        }

        body { background: var(--bg); color: var(--text); font-family: 'Barlow', sans-serif; min-height: 100vh; }

        .page { max-width: 960px; margin: 0 auto; padding: 40px 20px 80px; }

        .page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 48px; gap: 16px; flex-wrap: wrap;
        }
        .page-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: clamp(36px, 6vw, 58px); font-weight: 800;
          line-height: 1; letter-spacing: -0.5px; color: var(--text);
        }
        .page-title span { color: var(--accent); }
        .page-subtitle { color: var(--text2); margin-top: 6px; font-size: 15px; }

        .user-chip {
          display: flex; align-items: center; gap: 10px;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 50px; padding: 8px 14px 8px 8px;
        }
        .user-chip img { width: 32px; height: 32px; border-radius: 50%; }
        .user-chip__name { font-weight: 600; font-size: 14px; }
        .user-chip__badge {
          font-size: 11px; font-weight: 700; letter-spacing: .05em;
          text-transform: uppercase; padding: 2px 7px; border-radius: 4px;
        }
        .user-chip__badge--member { background: #22c55e22; color: var(--green); border: 1px solid #22c55e44; }
        .user-chip__badge--guest  { background: #f59e0b22; color: #f59e0b;      border: 1px solid #f59e0b44; }
        .logout-link { font-size: 12px; color: var(--text2); text-decoration: none; margin-left: 4px; background: none; border: none; cursor: pointer; }
        .logout-link:hover { color: var(--text); }

        .banner {
          border-radius: var(--radius); padding: 14px 18px; margin-bottom: 28px;
          font-size: 14px; font-weight: 500; display: flex; align-items: center; gap: 10px;
        }
        .banner--success { background: #22c55e18; border: 1px solid #22c55e44; color: var(--green); }
        .banner--cancel  { background: #ef444418; border: 1px solid #ef444444; color: var(--red); }
        .banner--info    { background: #4f8ef718; border: 1px solid #4f8ef744; color: var(--accent2); }

        .t-grid {
          display: grid; gap: 20px;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 420px), 1fr));
        }

        .t-card {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius); overflow: hidden;
          transition: border-color .2s, transform .2s;
        }
        .t-card:hover { border-color: #ffffff28; transform: translateY(-2px); }

        .t-card__header {
          display: flex; justify-content: space-between; align-items: flex-start;
          padding: 20px 20px 0;
        }
        .t-card__title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 24px; font-weight: 700; margin-top: 8px; line-height: 1.15;
        }
        .t-card__game { color: var(--text2); font-size: 13px; margin-top: 3px; }
        .t-card__meta { text-align: right; }
        .t-card__meta-item { display: flex; align-items: center; gap: 5px; color: var(--text2); font-size: 13px; }

        .t-card__body {
          padding: 16px 20px; display: flex; flex-direction: column; gap: 8px;
          border-top: 1px solid var(--border); margin-top: 16px;
        }
        .t-card__info-row {
          display: flex; justify-content: space-between; align-items: center; font-size: 13.5px;
        }
        .t-card__label { color: var(--text2); }
        .t-card__free  { color: var(--green); font-weight: 600; }
        .t-card__fee   { color: var(--accent); font-weight: 600; }

        .t-card__prizes {
          margin-top: 10px; padding-top: 12px; border-top: 1px dashed var(--border);
          display: flex; flex-direction: column; gap: 7px;
        }
        .t-card__prizes-label {
          font-size: 11px; text-transform: uppercase; letter-spacing: .06em;
          color: var(--text2); margin-bottom: 2px;
        }
        .prize-row {
          display: flex; align-items: center; gap: 9px; font-size: 13.5px; color: var(--text);
        }
        .prize-medal { font-size: 16px; line-height: 1; }

        .t-card__footer {
          display: flex; align-items: center; justify-content: flex-end; gap: 10px;
          padding: 14px 20px; border-top: 1px solid var(--border); flex-wrap: wrap;
        }

        .btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 18px; border-radius: 7px; font-size: 14px; font-weight: 600;
          cursor: pointer; border: none; text-decoration: none; transition: all .15s;
          font-family: inherit; white-space: nowrap;
        }
        .btn--primary  { background: var(--accent); color: #0d0f14; }
        .btn--primary:hover { background: #f0c050; }
        .btn--primary:disabled { background: var(--surface2); color: var(--text2); cursor: not-allowed; }
        .btn--discord  { background: var(--discord); color: #fff; }
        .btn--discord:hover { background: #4752c4; }
        .btn--steam    { background: #1b2838; color: #c7d5e0; border: 1px solid #2a3f5f; }
        .btn--steam:hover { background: #2a3f5f; }
        .btn--ghost    { background: transparent; color: var(--text2); border: 1px solid var(--border); }
        .btn--ghost:hover { color: var(--text); border-color: #ffffff30; }
        .btn--done     { background: #22c55e18; color: var(--green); border: 1px solid #22c55e44; cursor: default; }
        .btn--disabled { background: var(--surface2); color: var(--text2); cursor: default; }

        .empty { text-align: center; padding: 80px 20px; color: var(--text2); }
        .empty svg { margin-bottom: 16px; opacity: .35; }
        .empty h3 { font-family: 'Barlow Condensed', sans-serif; font-size: 24px; color: var(--text); margin-bottom: 6px; }

        .skeleton { background: var(--surface2); border-radius: var(--radius); height: 220px; animation: pulse 1.4s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }

        .login-banner {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 20px 24px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap; margin-bottom: 32px;
        }
        .login-banner p { color: var(--text2); font-size: 14px; }
        .login-banner strong { color: var(--text); }

        /* ── Modal ── */
        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.7);
          display: flex; align-items: center; justify-content: center;
          padding: 20px; z-index: 1000; backdrop-filter: blur(4px);
        }
        .modal {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: 14px; padding: 28px; max-width: 520px; width: 100%;
          max-height: 90vh; overflow-y: auto;
        }
        .modal__title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 28px; font-weight: 800; line-height: 1.1;
        }
        .modal__sub { color: var(--text2); font-size: 14px; margin-top: 2px; margin-bottom: 18px; }
        .modal__rules h4 {
          font-size: 13px; text-transform: uppercase; letter-spacing: .06em;
          color: var(--text2); margin-bottom: 10px;
        }
        .modal__rules ul { list-style: none; display: flex; flex-direction: column; gap: 10px; }
        .modal__rules li {
          font-size: 13.5px; line-height: 1.5; color: var(--text);
          padding-left: 18px; position: relative;
        }
        .modal__rules li::before {
          content: "•"; position: absolute; left: 4px; color: var(--accent);
        }
        .modal__rules strong { color: var(--accent); }
        .modal__accept {
          display: flex; align-items: flex-start; gap: 10px;
          margin: 20px 0; padding: 14px; background: var(--surface2);
          border-radius: 8px; cursor: pointer; font-size: 13.5px; line-height: 1.4;
        }
        .modal__accept input { margin-top: 2px; width: 16px; height: 16px; flex-shrink: 0; cursor: pointer; }
        .modal__actions { display: flex; gap: 10px; justify-content: flex-end; }

        /* ── Challengermode success-boks ── */
        .cm-box {
          background: linear-gradient(135deg, #22c55e15, #4f8ef715);
          border: 1px solid #22c55e44; border-radius: var(--radius);
          padding: 22px 24px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap;
        }
        .cm-box__text h3 { font-family: 'Barlow Condensed', sans-serif; font-size: 22px; color: var(--green); }
        .cm-box__text p { color: var(--text2); font-size: 13.5px; margin-top: 4px; max-width: 380px; }
      `}</style>

      <div className="page">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <TrophyIcon /> Turneringer<span>.</span>
            </h1>
            <p className="page-subtitle">
              Gratis for medlemmer · 20 kr for gæster · Registrering via Challengermode
            </p>
          </div>

          {user && (
            <div className="user-chip">
              {user.avatar && <img src={user.avatar} alt={user.username} />}
              <div>
                <div className="user-chip__name">{user.steam_username || user.username}</div>
                <span className={`user-chip__badge ${user.is_member ? "user-chip__badge--member" : "user-chip__badge--guest"}`}>
                  {user.is_member ? "Medlem" : "Gæst"}
                </span>
              </div>
              <button
                className="logout-link"
                onClick={async () => {
                  await fetch("/auth/logout", { credentials: "include" });
                  window.location.reload();
                }}
              >
                Log ud
              </button>
            </div>
          )}
        </div>

        {/* Challengermode success-boks efter tilmelding/betaling */}
        {confirmedTournament && (
          <div className="cm-box">
            <div className="cm-box__text">
              <h3>✓ Du er tilmeldt {confirmedTournament.name}!</h3>
              <p>
                Sidste skridt: registrér dig på Challengermode. Husk at checke ind
                senest 30 minutter før turneringsstart.
              </p>
            </div>
            {cmUrl ? (
              <a href={cmUrl} target="_blank" rel="noreferrer" className="btn btn--primary">
                <ExternalIcon /> Gå til Challengermode
              </a>
            ) : (
              <span className="btn btn--disabled">Link kommer snart</span>
            )}
          </div>
        )}

        {/* Betalings-bannere */}
        {payMsg === "cancel" && (
          <div className="banner banner--cancel">✕ Betaling annulleret. Prøv igen når du er klar.</div>
        )}

        {/* Login prompt */}
        {!user && !loading && (
          <div className="login-banner">
            <p><strong>Log ind med Discord</strong> for at se dit gebyr og tilmelde dig turneringer.</p>
            <a href="/auth/discord/login?type=tournament" className="btn btn--discord">
              <DiscordIcon /> Log ind med Discord
            </a>
          </div>
        )}

        {/* Mangler Steam-tilknytning */}
        {user && !user.steamid && (
          <div className="banner banner--info">
            🎮 Du mangler at tilknytte din Steam-konto for at kunne tilmelde dig turneringer.{" "}
            <a href="/auth/steam?return=/turneringer" style={{ color: "var(--accent2)", marginLeft: 6 }}>
              Tilknyt Steam →
            </a>
          </div>
        )}

        {/* Turnerings-grid */}
        {loading ? (
          <div className="t-grid">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton" />)}
          </div>
        ) : tournaments.length === 0 ? (
          <div className="empty">
            <TrophyIcon />
            <h3>Ingen turneringer endnu</h3>
            <p>Hold øje med denne side — nye turneringer annonceres snart.</p>
          </div>
        ) : (
          <div className="t-grid">
            {sortedTournaments.map((t) => (
              <TournamentCard key={t.id} tournament={t} user={user} onJoinClick={handleJoinClick} />
            ))}
          </div>
        )}

      </div>

      {/* Betingelses-modal */}
      {modalTournament && (
        <TermsModal
          tournament={modalTournament}
          user={user}
          onConfirm={handleConfirm}
          onClose={() => setModalTournament(null)}
        />
      )}
    </>
  );
}